import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { DataGridPremium } from '@mui/x-data-grid-premium'
import PropTypes, { any } from 'prop-types'
import { Box, Paper } from '../../../../../components/mui/Core/index'
import { fetchOrders, searchOrders, setSelectedOrder } from '../../order-slice'
import LinkButton from '../../../../../components/LinkButton/link-button'
import { useTheme } from '../../../../../components/mui/Styles/index'
import GenericSnackbar from '../../../../../components/GenericSnackbar/generic-snackbar'
import GenericTablePagination from '../../../../../components/GenericTablePagination/generic-table-pagination'
import StatusChip from '../../../../../components/genericcomponents/StatusChip/status-chip'
import { setSnackbar } from '../../../../../components/GenericSnackbar/generic-snackbar-slice'
import Constants from '../../../../../constants/en.json'
import { hasUserPermission } from '../../../../../utils/user-utils'
import { userPermissionConstants } from '../../../../../constants/user-permission-constants'
import TableToolBar from '../../../../../components/TableToolBar/table-toolbar'
import NoResultsFoundTableOverlay from '../../../../../components/NoResultsFoundTableOverlay/no-results-found-table-overlay'
import { getDateTimeFormattedValue } from '../../../../../utils/string-utils'

export function WmsOrdersTable({ filterBy, filterValue, selectedClient, searchByOptions }) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const theme = useTheme()
  const authenticatedUser = useSelector((state) => state.login.authenticatedUser)
  const isFetchingOrders = useSelector((state) => state.order.isFetchingOrders)
  const orders = useSelector((state) => state.order.orders)
  const [pageSize, setPageSize] = useState(25)
  const [page, setPage] = useState(0)
  const totalOrdersCount = useSelector((state) => state.order.totalOrdersCount)
  const [sortDirection, setSortDirection] = useState('desc')
  const [sortProperty, setSortProperty] = useState('createdOn')
  const fetchOrdersError = useSelector((state) => state.order.fetchOrdersError)
  const hasSwitchClientPermission = hasUserPermission({
    permissionCode: userPermissionConstants.SWITCH_CLIENT,
    permissions: authenticatedUser?.permissions,
  })
  const [newFilterValue, setNewFilterValue] = useState('')
  const previousFilterValue = useRef()
  const [isFilterValueSame, setIsFilterValueSame] = useState()
  const selectedSearchOption = searchByOptions.filter((option) => {
    return option.id === filterBy
  })
  const filterByDescription = selectedSearchOption[0]?.text || ''

  useEffect(() => {
    previousFilterValue.current = filterValue
  }, [filterValue])

  useEffect(() => {
    const isNewSearch = previousFilterValue.current === newFilterValue
    setIsFilterValueSame(previousFilterValue.current === newFilterValue)

    if (!hasSwitchClientPermission || (hasSwitchClientPermission && selectedClient)) {
      if (filterValue) {
        const searchvalue = {
          page: isNewSearch ? page : 0,
          pageSize,
          sortDirection,
          sortProperty,
          filterBy: filterBy === 'orderStatus' ? 'status' : filterBy,
          filterValue,
        }
        dispatch(searchOrders(searchvalue))
      } else {
        const value = { page, pageSize, sortDirection, sortProperty }
        dispatch(fetchOrders(value))
      }
    }
  }, [
    dispatch,
    filterBy,
    filterValue,
    selectedClient,
    hasSwitchClientPermission,
    page,
    pageSize,
    sortDirection,
    sortProperty,
    newFilterValue,
  ])

  useEffect(() => {
    /* When error occurs, display snackbar */
    if (fetchOrdersError) {
      dispatch(
        setSnackbar({
          message: fetchOrdersError?.message,
          severity: 'error',
          action: true,
        }),
      )
    }
  }, [fetchOrdersError, dispatch])

  const handleChangePage = (event, newPage) => {
    setPage(Number(newPage))
    setNewFilterValue(filterValue)
  }

  const handleChangeRowsPerPage = (event) => {
    setPageSize(parseInt(event.target.value, 10))
    setPage(0)
    setNewFilterValue(filterValue)
  }

  const handleOrderLinkClick = (order) => {
    dispatch(setSelectedOrder(order))
    navigate('/orderdetails')
  }

  const handleSortChange = (sort) => {
    setNewFilterValue(filterValue)
    if (sort.length === 0) {
      setSortDirection('desc')
      return
    }
    if (sort[0]?.field !== 'shipToName') {
      setSortProperty(sort[0].field)
    }
    if (sort[0]?.field === 'shipToName') {
      setSortProperty('ship_to_name')
    }
    if (sort.length > 0) {
      setSortDirection(sort[0].sort)
    }
  }

  const rows = orders.map((row) => ({
    id: row.wms_order,
    wms_order: row.wms_order,
    po: row.ship_po,
    numLine: row.num_line,
    createdOn: row.createdOn,
    orderStatus: row.orderStatus,
    shipToName: row.ship_to_custname,
    shipToAccount: row.ship_to_custnum,
    dateShipped: row.dateShipped,
  }))

  const columns = [
    {
      field: 'wms_order',
      headerName: Constants.orders.ORDER,
      flex: 0.5,
      minWidth: 110,
      renderCell: (cellValues) => {
        const wmsOrder = cellValues.row.wms_order
        return (
          <LinkButton
            handleLinkClick={() => {
              handleOrderLinkClick(cellValues.row)
            }}
            linkText={String(wmsOrder)}
          />
        )
      },
    },
    {
      field: 'po',
      headerName: Constants.orders.PO_NUMBER,
      flex: 0.4,
      minWidth: 120,
      valueGetter: (value) => {
        return value || 'None'
      },
    },
    {
      field: 'numLine',
      headerName: Constants.orders.LINES,
      flex: 0.2,
      minWidth: 80,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'createdOn',
      headerName: Constants.orders.CREATED_ON,
      flex: 0.4,
      minWidth: 200,
      align: 'right',
      headerAlign: 'right',
      renderCell: (cellValues) => {
        return getDateTimeFormattedValue(cellValues.value) || 'Pending'
      },
    },
    {
      field: 'orderStatus',
      headerName: Constants.orders.STATUS,
      flex: 0.5,
      minWidth: 180,
      sortable: false,
      renderCell: (cellValues) => {
        return <StatusChip status={cellValues.row.orderStatus} />
      },
    },
    { field: 'shipToName', headerName: Constants.orders.SHIP_TO_NAME, flex: 0.6, minWidth: 180 },
    {
      field: 'shipToAccount',
      headerName: Constants.orders.SHIP_TO_ACCOUNT,
      flex: 0.4,
      minWidth: 180,
    },
    {
      field: 'dateShipped',
      headerName: Constants.orders.DATE_SHIPPED,
      flex: 0.5,
      minWidth: 220,
      renderCell: (cellValues) => {
        return getDateTimeFormattedValue(cellValues.value) || 'Pending'
      },
      align: 'right',
      headerAlign: 'right',
    },
  ]

  const gridStyle = {
    minHeight: '160px',
    height: isFetchingOrders ? '400px' : 'auto',
    backgroundColor: theme.palette.common.white,
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: theme.palette.grey[300],
    '& .MuiDataGrid-cell': {
      paddingLeft: '30px',
    },
    '& .MuiDataGrid-columnHeader': {
      paddingLeft: '30px',
    },
  }

  return (
    <Box>
      <Paper>
        <DataGridPremium
          sx={gridStyle}
          rows={rows}
          columns={columns}
          loading={isFetchingOrders}
          spacing={1}
          sortingOrder={['asc', 'desc', null]}
          onSortModelChange={(newSortModel) => handleSortChange(newSortModel)}
          rowCount={Number(totalOrdersCount)}
          paginationMode='server'
          sortingMode='server'
          slotProps={{
            toolbar: { rows },
            pagination: {
              count: Number(totalOrdersCount),
              page: totalOrdersCount <= pageSize || !isFilterValueSame ? 0 : Number(page),
              rowsPerPage: pageSize,
              onPageChange: handleChangePage,
              onRowsPerPageChange: handleChangeRowsPerPage,
            },
            noRowsOverlay: {
              filterByDescription,
              noResultsValue: filterValue,
            },
          }}
          slots={{
            noRowsOverlay: NoResultsFoundTableOverlay,
            pagination: GenericTablePagination,
            toolbar: TableToolBar,
          }}
          pagination
          disableColumnFilter
          disableRowSelectionOnClick
          showToolbar
          disableColumnReorder
        />
      </Paper>
      <GenericSnackbar />
    </Box>
  )
}

WmsOrdersTable.propTypes = {
  filterBy: PropTypes.string.isRequired,
  filterValue: PropTypes.string.isRequired,
  selectedClient: PropTypes.string.isRequired,
  searchByOptions: PropTypes.arrayOf(PropTypes.objectOf(any)).isRequired,
}

export default WmsOrdersTable
